import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, RadialBarChart, RadialBar, AreaChart, Area } from "recharts";
import { PieChart as PieIcon, Users, Receipt, Target } from "lucide-react";
import api, { formatCurrency } from "@/lib/api";

/**
 * Tres mini-gráficas adicionales:
 * 1. Obras por estado (donut)
 * 2. Tareas por trabajador (barras horizontales)
 * 3. Facturación últimos 6 meses (barras agrupadas Emitida vs Cobrada)
 */
export default function DashboardMiniCharts() {
  const [data, setData] = useState(null);
  const [plazos, setPlazos] = useState(null);
  const [ventanaIdx, setVentanaIdx] = useState(2); // 0=30d, 1=60d, 2=90d

  useEffect(() => {
    api.get("/dashboard/mini_charts")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    api.get("/dashboard/cumplimiento_plazos")
      .then((r) => setPlazos(r.data))
      .catch(() => setPlazos(null));
  }, []);

  if (!data) return null;

  const obras = data.obras_por_estado || [];
  const trabajadores = data.tareas_por_trabajador || [];
  const facturas = data.facturacion_mensual || [];

  const fmtAxis = (v) => {
    if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
    return v;
  };

  const tooltipStyle = {
    background: "var(--legatum-surface)",
    border: "1px solid var(--legatum-border)",
    borderRadius: 8,
    color: "var(--legatum-text)",
  };
  const labelStyle = { color: "var(--legatum-text-muted)", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 };

  const cardStyle = {
    background: "var(--legatum-surface)",
    border: "1px solid var(--legatum-border)",
  };

  const totalObras = obras.reduce((acc, o) => acc + o.count, 0);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-8 md:mb-10"
      data-testid="dashboard-mini-charts"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[var(--legatum-accent)]/15 text-[var(--legatum-accent)] border border-[var(--legatum-accent)]/30">
          <PieIcon className="h-3.5 w-3.5" />
        </div>
        <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-[var(--legatum-text-muted)]">
          Métricas clave
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* 1. Obras por estado — donut */}
        <div className="rounded-2xl p-4 md:p-5" style={cardStyle} data-testid="chart-obras-estado">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#0F8A5F]/15 text-[#0F8A5F] border border-[#0F8A5F]/30">
              <PieIcon className="h-3.5 w-3.5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--legatum-text-muted)]">Obras por estado</div>
              <div className="text-xs text-[var(--legatum-text-secondary)]">
                <span className="font-bold text-[var(--legatum-text)] tabular-nums">{totalObras}</span> en cartera
              </div>
            </div>
          </div>
          {obras.length === 0 ? (
            <div className="text-center text-xs text-[var(--legatum-text-muted)] py-10">Sin obras todavía.</div>
          ) : (
            <div className="h-52 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={obras} dataKey="count" nameKey="label" innerRadius={50} outerRadius={75} paddingAngle={3} stroke="var(--legatum-surface)" strokeWidth={3} animationDuration={1200}>
                    {obras.map((o) => (
                      <Cell key={o.estado} fill={o.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v} obras`} labelStyle={labelStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <div className="text-2xl font-extrabold tabular-nums text-[var(--legatum-text)] leading-none">{totalObras}</div>
                  <div className="text-[9px] uppercase tracking-widest text-[var(--legatum-text-muted)] mt-0.5 font-bold">Obras</div>
                </div>
              </div>
            </div>
          )}
          {/* Leyenda compacta */}
          <ul className="mt-2 space-y-1">
            {obras.slice(0, 4).map((o) => (
              <li key={o.estado} className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: o.color }} />
                  <span className="text-[var(--legatum-text-secondary)] font-medium truncate">{o.label}</span>
                </span>
                <span className="font-bold tabular-nums text-[var(--legatum-text)]">{o.count}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 2. Tareas por trabajador — barras horizontales */}
        <div className="rounded-2xl p-4 md:p-5" style={cardStyle} data-testid="chart-tareas-trabajador">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#6D43B8]/15 text-[#6D43B8] border border-[#6D43B8]/30">
              <Users className="h-3.5 w-3.5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--legatum-text-muted)]">Tareas por trabajador</div>
              <div className="text-xs text-[var(--legatum-text-secondary)]">Completadas · Pdtes · Atrasadas</div>
            </div>
          </div>
          {trabajadores.length === 0 ? (
            <div className="text-center text-xs text-[var(--legatum-text-muted)] py-10">Sin tareas asignadas todavía.</div>
          ) : (
            <div style={{ height: Math.max(160, trabajadores.length * 28 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={trabajadores.map((t) => ({
                    name: (t.name || "").split(" ")[0] || "—",
                    Completadas: t.completadas,
                    Pendientes: Math.max(0, t.total - t.completadas - t.atrasadas),
                    Atrasadas: t.atrasadas,
                  }))}
                  layout="vertical"
                  margin={{ top: 4, right: 8, left: 4, bottom: 4 }}
                  barCategoryGap={6}
                >
                  <CartesianGrid stroke="var(--legatum-border)" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "var(--legatum-text-muted)", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "var(--legatum-border)" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "var(--legatum-text-secondary)", fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={{ stroke: "var(--legatum-border)" }} width={70} />
                  <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} />
                  <Legend wrapperStyle={{ fontSize: 10, fontWeight: 600 }} iconType="circle" />
                  <Bar dataKey="Completadas" stackId="a" fill="url(#completadasGrad)" radius={[0, 6, 6, 0]} animationDuration={900} />
                  <Bar dataKey="Pendientes" stackId="a" fill="url(#pendientesGrad)" animationDuration={900} animationBegin={100} />
                  <Bar dataKey="Atrasadas" stackId="a" fill="url(#atrasadasGrad)" radius={[0, 6, 6, 0]} animationDuration={900} animationBegin={200} />
                  <defs>
                    <linearGradient id="completadasGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#0F8A5F" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#0F8A5F" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="pendientesGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#C9A24A" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#C9A24A" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="atrasadasGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#DC2626" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#DC2626" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* 3. Facturación últimos 6 meses */}
        <div className="rounded-2xl p-4 md:p-5" style={cardStyle} data-testid="chart-facturacion">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#127372]/15 text-[#127372] border border-[#127372]/30">
              <Receipt className="h-3.5 w-3.5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--legatum-text-muted)]">Facturación · 6 meses</div>
              <div className="text-xs text-[var(--legatum-text-secondary)]">
                Total emitido:&nbsp;
                <span className="font-bold tabular-nums text-[var(--legatum-text)]">
                  {formatCurrency(facturas.reduce((acc, f) => acc + f.emitida, 0))}
                </span>
              </div>
            </div>
          </div>
          {facturas.length === 0 || facturas.every((f) => f.emitida === 0) ? (
            <div className="text-center text-xs text-[var(--legatum-text-muted)] py-10">Sin facturación emitida en los últimos 6 meses.</div>
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={facturas.map((f) => ({ mes: f.label, Emitida: f.emitida, Cobrada: f.cobrada }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="var(--legatum-border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fill: "var(--legatum-text-muted)", fontSize: 10, fontWeight: 700 }} tickLine={false} axisLine={{ stroke: "var(--legatum-border)" }} />
                  <YAxis tickFormatter={fmtAxis} tick={{ fill: "var(--legatum-text-muted)", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "var(--legatum-border)" }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} labelStyle={labelStyle} />
                  <Legend wrapperStyle={{ fontSize: 10, fontWeight: 600 }} iconType="circle" />
                  <Bar dataKey="Emitida" fill="url(#emitidaGrad)" radius={[6, 6, 0, 0]} maxBarSize={32} animationDuration={900} />
                  <Bar dataKey="Cobrada" fill="url(#cobradaGrad)" radius={[6, 6, 0, 0]} maxBarSize={32} animationDuration={900} animationBegin={150} />
                  <defs>
                    <linearGradient id="emitidaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#127372" stopOpacity={1} />
                      <stop offset="100%" stopColor="#127372" stopOpacity={0.5} />
                    </linearGradient>
                    <linearGradient id="cobradaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0F8A5F" stopOpacity={1} />
                      <stop offset="100%" stopColor="#0F8A5F" stopOpacity={0.5} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* 4. Cumplimiento de plazos — gauge radial + sparkline */}
        <div className="rounded-2xl p-4 md:p-5" style={cardStyle} data-testid="chart-cumplimiento-plazos">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#E08A1F]/15 text-[#E08A1F] border border-[#E08A1F]/30 shrink-0">
                <Target className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--legatum-text-muted)] truncate">Cumplimiento plazos</div>
                <div className="text-xs text-[var(--legatum-text-secondary)]">Tareas a tiempo</div>
              </div>
            </div>
            {/* Selector 30/60/90 */}
            <div className="inline-flex rounded-md border border-[var(--legatum-border)] bg-[var(--legatum-elevated)] p-0.5 text-[9px] font-bold uppercase tracking-widest shrink-0">
              {[0, 1, 2].map((i) => {
                const v = plazos?.ventanas?.[i];
                if (!v) return null;
                return (
                  <button
                    key={v.dias}
                    onClick={() => setVentanaIdx(i)}
                    data-testid={`cumplimiento-window-${v.dias}`}
                    className={`px-1.5 py-0.5 rounded transition-colors ${
                      ventanaIdx === i
                        ? "bg-[var(--legatum-accent)] text-[var(--legatum-accent-fg)]"
                        : "text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)]"
                    }`}
                  >
                    {v.dias}d
                  </button>
                );
              })}
            </div>
          </div>

          {(() => {
            if (!plazos) return <div className="text-center text-xs text-[var(--legatum-text-muted)] py-10">Cargando…</div>;
            const v = plazos.ventanas[ventanaIdx] || plazos.ventanas[2];
            if (!v || v.total === 0) {
              return <div className="text-center text-xs text-[var(--legatum-text-muted)] py-10">Sin tareas con plazo en los últimos {v?.dias || 90} días.</div>;
            }
            const pct = v.pct;
            const color = pct >= 85 ? "#0F8A5F" : pct >= 60 ? "#E08A1F" : "#DC2626";
            const gaugeData = [{ name: "pct", value: pct, fill: color }];

            return (
              <>
                <div className="h-32 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                      innerRadius="68%"
                      outerRadius="100%"
                      data={gaugeData}
                      startAngle={210}
                      endAngle={-30}
                    >
                      <RadialBar background={{ fill: "var(--legatum-elevated)" }} dataKey="value" cornerRadius={8} domain={[0, 100]} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center">
                      <div className="text-3xl font-extrabold tabular-nums leading-none" style={{ color }}>
                        {pct.toFixed(0)}<span className="text-base">%</span>
                      </div>
                      <div className="text-[9px] uppercase tracking-widest text-[var(--legatum-text-muted)] mt-0.5 font-bold">A tiempo</div>
                    </div>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="mt-2 grid grid-cols-3 gap-1 text-center">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--legatum-text-muted)]">A tiempo</div>
                    <div className="text-sm font-bold tabular-nums text-[#0F8A5F]">{v.a_tiempo}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--legatum-text-muted)]">Tarde</div>
                    <div className="text-sm font-bold tabular-nums text-[#E08A1F]">{v.tarde}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--legatum-text-muted)]">Atrasadas</div>
                    <div className="text-sm font-bold tabular-nums text-[#DC2626]">{v.atrasadas}</div>
                  </div>
                </div>

                {/* Sparkline últimas 8 semanas */}
                {plazos.sparkline && plazos.sparkline.some((s) => s.pct !== null) && (
                  <div className="mt-3">
                    <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--legatum-text-muted)] mb-1">Tendencia 8 semanas</div>
                    <div className="h-12">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={plazos.sparkline.map((s) => ({ x: s.label, pct: s.pct ?? 0 }))} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                          <defs>
                            <linearGradient id="sparkPct" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                              <stop offset="100%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Tooltip
                            contentStyle={tooltipStyle}
                            formatter={(val) => `${val}%`}
                            labelStyle={labelStyle}
                          />
                          <Area type="monotone" dataKey="pct" stroke={color} strokeWidth={2} fill="url(#sparkPct)" dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </motion.section>
  );
}
